// =============================================================================
// Capa de API: wrappers tipados de los RPC del backend (db/rpc/*).
// Un solo lugar donde el frontend habla con Postgres. Cada función mapea 1:1 a
// un RPC SECURITY DEFINER ya probado. NUNCA se hace insert/update directo de
// dinero/stock/fiscal: todo pasa por aquí.
// =============================================================================
import { supabase } from "./supabase";

/** Lanza si el RPC devolvió error; si no, retorna el data tipado.
 *  El cast a never sortea el tipado laxo de database.types.ts (placeholder);
 *  al correr `npm run gen:types` los RPC quedan fuertemente tipados. */
async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  // deno-lint-ignore no-explicit-any
  const { data, error } = await supabase.rpc(fn as never, (args ?? {}) as never);
  if (error) throw new ApiError(error.message, fn, error.code);
  return data as T;
}

export class ApiError extends Error {
  constructor(message: string, public fn: string, public code?: string) {
    super(message);
    this.name = "ApiError";
  }
  /** true si el error es un tope de plan (el SPA muestra "sube de plan"). */
  get isPlanLimit() {
    return this.message.includes("Límite del plan") || this.message.includes("Límite de documentos");
  }
}

// ----- tipos de payload (espejo de los args de los RPC) ----------------------
export interface PaymentInput { method: string; amount: number; reference?: string | null; status?: string }

// ----- POS / venta -----------------------------------------------------------
export const pos = {
  /** Pistolear: resuelve producto+presentación+precio+stock por código. */
  resolveBarcode: (branchId: string, code: string) =>
    rpc<PosResolved>("pos_resolve_barcode", { p_branch_id: branchId, p_code: code }),

  openSale: (branchId: string, clientUuid: string, opts?: { cashSessionId?: string; customerId?: string; priceListId?: string }) =>
    rpc<Sale>("pos_open_sale", {
      p_branch_id: branchId, p_client_uuid: clientUuid,
      p_cash_session_id: opts?.cashSessionId ?? null,
      p_customer_id: opts?.customerId ?? null,
      p_price_list_id: opts?.priceListId ?? null,
    }),

  /** Agrega línea; el ITBMS y totales los calcula el SERVIDOR.
   *  `clientUuid` hace idempotencia (sync no duplica al reintentar).
   *  `lineNote` describe un ítem libre ("cortar tubo a 2.5m"). */
  addLine: (saleId: string, productUnitId: string, qty: number, unitPrice?: number, discount = 0, clientUuid?: string, lineNote?: string) =>
    rpc<SaleLine>("pos_add_line", {
      p_sale_id: saleId, p_product_unit_id: productUnitId, p_qty: qty,
      p_unit_price: unitPrice ?? null, p_discount: discount,
      p_client_uuid: clientUuid ?? null,
      p_line_note: lineNote ?? null,
    }),
  /** Devuelve el product_unit_id del producto especial "Ítem libre" (lo crea idempotente). */
  ensureOpenItem: () => rpc<string>("pos_ensure_open_item", {}),

  /** Recalcula totales (subtotal + ITBMS + total). El server es la fuente. */
  recomputeTotals: (saleId: string) => rpc<Sale>("sale_recompute_totals", { p_sale_id: saleId }),

  /** Suspende una venta abierta. status=parked, etiqueta visible para retomarla. */
  parkSale: (saleId: string, label?: string) =>
    rpc<Sale>("pos_park_sale", { p_sale_id: saleId, p_label: label ?? null }),
  /** Convierte la venta en cotización (status=quote) con vigencia en días. */
  quoteSale: (saleId: string, label?: string, validDays = 15) =>
    rpc<Sale>("pos_quote_sale", { p_sale_id: saleId, p_label: label ?? null, p_valid_days: validDays }),
  /** Retoma una venta parked/quote → status=open. */
  resumeSale: (saleId: string) => rpc<Sale>("pos_resume_sale", { p_sale_id: saleId }),
  /** Descuento absoluto en una línea (recalcula ITBMS y totales). */
  setLineDiscount: (lineId: string, discount: number) =>
    rpc<SaleLine>("pos_set_line_discount", { p_line_id: lineId, p_discount: discount }),
  /** Descuento absoluto al total — se prorratea entre las líneas (DGI). */
  setTotalDiscount: (saleId: string, discount: number) =>
    rpc<Sale>("pos_set_total_discount", { p_sale_id: saleId, p_discount: discount }),
  /** Ventas suspendidas/cotizaciones de la sucursal activa. */
  listParked: async (branchId: string): Promise<ParkedSale[]> => {
    const { data, error } = await supabase
      .from("sales")
      .select("id,park_label,parked_at,total,status,created_at")
      .eq("branch_id", branchId)
      .in("status", ["parked", "quote"])
      .order("parked_at", { ascending: false });
    if (error) throw new ApiError(error.message, "list-parked");
    return (data ?? []) as unknown as ParkedSale[];
  },

  removeLine: (saleLineId: string) => rpc<void>("pos_remove_line", { p_sale_line_id: saleLineId }),

  /** Cierre atómico e idempotente por (org, clientUuid). */
  finalizeSale: (saleId: string, clientUuid: string, payments: PaymentInput[], changeGiven = 0) =>
    rpc<Sale>("pos_finalize_sale", {
      p_sale_id: saleId, p_client_uuid: clientUuid,
      p_payments: payments, p_change_given: changeGiven,
    }),
};

// ----- catálogo / inventario -------------------------------------------------
export const catalog = {
  list: (branchId: string, search?: string, limit = 50, offset = 0, categoryId?: string | null, onlyActive = true, priceListId?: string | null) =>
    rpc<CatalogItem[]>("catalog_list", {
      p_branch_id: branchId, p_search: search ?? null,
      p_limit: limit, p_offset: offset, p_only_active: onlyActive,
      p_category_id: categoryId ?? null,
      p_price_list_id: priceListId ?? null,
    }),
  upsertProduct: (p: ProductUpsert) =>
    rpc<Product>("product_upsert", {
      p_id: p.id ?? null, p_sku: p.sku, p_name: p.name, p_base_unit_id: p.baseUnitId,
      p_itbms_rate: p.itbmsRate ?? "rate_7", p_category_id: p.categoryId ?? null, p_brand: p.brand ?? null,
      p_tracks_stock: p.tracksStock ?? true, p_tracks_lot: p.tracksLot ?? false,
      p_allows_fraction: p.allowsFraction ?? true, p_unspsc_code: p.unspscCode ?? null, p_is_active: p.isActive ?? true,
    }),
  /** Crea/versiona una presentación (unidad de venta) de un producto. */
  upsertUnit: (productId: string, unitId: string, factor: number, opts?: { id?: string; sellable?: boolean; purchasable?: boolean }) =>
    rpc<{ id: string }>("product_unit_upsert", {
      p_id: opts?.id ?? null, p_product_id: productId, p_unit_id: unitId, p_factor: factor,
      p_is_sellable: opts?.sellable ?? true, p_is_purchasable: opts?.purchasable ?? true,
    }),
  setPrice: (priceListId: string, productUnitId: string, price: number, minQty = 0) =>
    rpc<unknown>("product_price_set", { p_price_list_id: priceListId, p_product_unit_id: productUnitId, p_price: price, p_min_qty: minQty }),
  linkBarcode: (productUnitId: string, code: string, source = "factory") =>
    rpc<unknown>("barcode_link", { p_product_unit_id: productUnitId, p_code: code, p_source: source }),
  generateInternalLabel: (productUnitId: string) =>
    rpc<string>("product_generate_internal_label", { p_product_unit_id: productUnitId }),
};

export const inventory = {
  stockList: (branchId: string, lowOnly = false, limit = 100, offset = 0) =>
    rpc<StockRow[]>("stock_list", { p_branch_id: branchId, p_low_only: lowOnly, p_limit: limit, p_offset: offset }),
  openCount: (branchId: string, notes?: string) =>
    rpc<unknown>("inventory_count_open", { p_branch_id: branchId, p_notes: notes ?? null }),
  applyCount: (countId: string, lines: { product_id: string; qty_counted: number }[]) =>
    rpc<unknown>("inventory_count_apply", { p_count_id: countId, p_lines: lines }),
  adjust: (branchId: string, productId: string, qtyBase: number, reason: string) =>
    rpc<unknown>("stock_adjust", { p_branch_id: branchId, p_product_id: productId, p_qty_base: qtyBase, p_reason: reason }),
  transferCreate: (from: string, to: string, lines: { product_id: string; qty_base: number }[], notes?: string) =>
    rpc<unknown>("stock_transfer_create", { p_from_branch: from, p_to_branch: to, p_lines: lines, p_notes: notes ?? null }),
  transferShip: (id: string) => rpc<unknown>("stock_transfer_ship", { p_transfer_id: id }),
  transferReceive: (id: string, lines?: { product_id: string; qty_received: number }[]) =>
    rpc<unknown>("stock_transfer_receive", { p_transfer_id: id, p_lines: lines ?? null }),
};

// ----- compras ---------------------------------------------------------------
export const procurement = {
  poCreate: (supplierId: string, branchId: string, lines: PoLine[], expectedAt?: string) =>
    rpc<unknown>("purchase_order_create", { p_supplier_id: supplierId, p_branch_id: branchId, p_lines: lines, p_expected_at: expectedAt ?? null }),
  poSend: (id: string) => rpc<unknown>("purchase_order_send", { p_po_id: id }),
  /** Recepción dura (idempotente por clientUuid). */
  receive: (poId: string | null, lines: ReceiptLine[], clientUuid: string, opts?: ReceiveOpts) =>
    rpc<unknown>("procurement_receive", {
      p_po_id: poId, p_lines: lines, p_client_uuid: clientUuid,
      p_supplier_id: opts?.supplierId ?? null, p_branch_id: opts?.branchId ?? null,
      p_invoice_no: opts?.invoiceNo ?? null, p_invoice_date: opts?.invoiceDate ?? null, p_invoice_total: opts?.invoiceTotal ?? null,
    }),
  supplierUpsert: (s: SupplierUpsert) =>
    rpc<unknown>("supplier_upsert", {
      p_id: s.id ?? null, p_name: s.name, p_ruc: s.ruc ?? null, p_dv: s.dv ?? null,
      p_phone: s.phone ?? null, p_email: s.email ?? null,
      p_payment_terms_days: s.paymentTermsDays ?? 0, p_lead_time_days: s.leadTimeDays ?? 7,
    }),
};

// ----- caja ------------------------------------------------------------------
export const cash = {
  open: (branchId: string, openingAmount = 0) => rpc<CashSession>("cash_open_session", { p_branch_id: branchId, p_opening_amount: openingAmount }),
  close: (sessionId: string, countedCash: number, threshold = 5) =>
    rpc<CashSession>("cash_close_session", { p_session_id: sessionId, p_counted_cash: countedCash, p_threshold: threshold }),
  movement: (sessionId: string, direction: "in" | "out", amount: number, reason: string) =>
    rpc<unknown>("cash_movement_add", { p_session_id: sessionId, p_direction: direction, p_amount: amount, p_reason: reason }),
};

// ----- clientes / crédito ----------------------------------------------------
export const crm = {
  /** Búsqueda paginada de clientes por nombre / RUC / cédula / teléfono.
   *  Diseñada para 1000s de clientes: server-side, ilike, paginada. */
  searchCustomers: async ({ search, limit = 25, offset = 0 }: { search?: string; limit?: number; offset?: number }): Promise<Customer[]> => {
    const cols = "id,name,ruc,dv,phone,email,address,tax_receiver_type,commercial_segment,price_list_id,cedula";
    let q = supabase.from("customers").select(cols).eq("is_active", true).order("name").range(offset, offset + limit - 1);
    const s = search?.trim();
    if (s) {
      const safe = s.replace(/[,()*]/g, " ").trim();
      q = q.or(`name.ilike.%${safe}%,ruc.ilike.%${safe}%,cedula.ilike.%${safe}%,phone.ilike.%${safe}%`);
    }
    const { data, error } = await q;
    if (error) throw new ApiError(error.message, "search-customers");
    return (data ?? []) as unknown as Customer[];
  },
  /** Atajo compat: primeras 50 con search (deprecado, usa searchCustomers). */
  listCustomers: (search?: string): Promise<Customer[]> => crm.searchCustomers({ search, limit: 50 }),
  /** Lista de categorías (para tabs del POS). */
  listCategories: async (): Promise<{ id: string; name: string }[]> => {
    const { data, error } = await supabase.from("categories").select("id,name").order("name");
    if (error) throw new ApiError(error.message, "list-categories");
    return (data ?? []) as { id: string; name: string }[];
  },
  upsertCustomer: (c: CustomerUpsert) =>
    rpc<Customer>("customer_upsert", {
      p_id: c.id ?? null, p_name: c.name, p_tax_receiver_type: c.taxReceiverType ?? "consumidor_final",
      p_commercial_segment: c.commercialSegment ?? "minorista", p_ruc: c.ruc ?? null, p_dv: c.dv ?? null,
      p_cedula: c.cedula ?? null, p_phone: c.phone ?? null, p_email: c.email ?? null,
      p_address: c.address ?? null, p_price_list_id: c.priceListId ?? null,
    }),
  setCreditAccount: (customerId: string, limit: number, hardLimit?: number, graceDays = 30) =>
    rpc<unknown>("credit_account_set", { p_customer_id: customerId, p_credit_limit: limit, p_hard_limit: hardLimit ?? null, p_grace_days: graceDays }),
  applyPayment: (customerId: string, amount: number, clientUuid: string, method = "cash") =>
    rpc<unknown>("credit_payment_apply", { p_customer_id: customerId, p_amount: amount, p_client_uuid: clientUuid, p_method: method }),
  statement: (customerId: string) => rpc<CustomerStatement>("customer_statement", { p_customer_id: customerId }),
};

// ----- fiscal ----------------------------------------------------------------
export const fiscal = {
  enqueue: (saleId: string, docType = "factura_interna", emissionMode?: "previa" | "posterior") =>
    rpc<FiscalDoc>("fiscal_enqueue_document", { p_sale_id: saleId, p_doc_type: docType, p_emission_mode: emissionMode ?? null }),
  /** Emisión ante el PAC (Edge Function, no RPC). */
  emit: async (saleId: string, docType = "factura_interna") => {
    const { data, error } = await supabase.functions.invoke("pac-emit", { body: { saleId, docType } });
    if (error) throw new ApiError(error.message, "pac-emit");
    return data;
  },
  setContingency: (terminalId: string, on: boolean) => rpc<unknown>("terminal_set_contingency", { p_terminal_id: terminalId, p_on: on }),
  processReturn: (saleId: string, clientUuid: string, lines: ReturnLine[], reason?: string, emitNc = true) =>
    rpc<unknown>("process_sale_return", { p_sale_id: saleId, p_client_uuid: clientUuid, p_lines: lines, p_reason: reason ?? null, p_emit_nc: emitNc }),
};

// ----- admin / plan ----------------------------------------------------------
export const admin = {
  branchCreate: (name: string, code: string, address?: string, phone?: string) =>
    rpc<unknown>("branch_create", { p_name: name, p_code: code, p_address: address ?? null, p_phone: phone ?? null }),
  terminalCreate: (branchId: string, code: string, name: string) =>
    rpc<unknown>("terminal_create", { p_branch_id: branchId, p_code: code, p_name: name }),
  userInvite: (authUserId: string, fullName: string, email: string, role: string, branchIds?: string[]) =>
    rpc<unknown>("user_invite", { p_auth_user_id: authUserId, p_full_name: fullName, p_email: email, p_role: role, p_branch_ids: branchIds ?? null }),
  planLimits: (tier: string) => rpc<PlanLimits>("plan_limits", { p_tier: tier }),
  signatureMeta: (subject: string, issuedAt: string, serial?: string) =>
    rpc<unknown>("signature_set_metadata", { p_subject: subject, p_serial: serial ?? null, p_issued_at: issuedAt }),
  /** Alta de ferretería (tenant) vía Edge Function signup (corre como service_role). */
  signup: async (body: {
    authUserId: string; email: string; fullName: string;
    legalName: string; tradeName: string; ruc: string; dv: string;
  }) => {
    const { data, error } = await supabase.functions.invoke("signup", { body });
    if (error) throw new ApiError(error.message, "signup");
    return data as { organizationId: string; role: string };
  },
};

// ----- licenciamiento (app nativa) -------------------------------------------
export const license = {
  validate: (machineId: string) => rpc<{ valid: boolean; reason?: string; status?: string; branch_id?: string }>("license_validate", { p_machine_id: machineId }),
  activate: (machineId: string, branchId?: string, label?: string) =>
    rpc<unknown>("license_activate", { p_machine_id: machineId, p_branch_id: branchId ?? null, p_label: label ?? null }),
  revoke: (licenseId: string) => rpc<unknown>("license_revoke", { p_license_id: licenseId }),
};

// ----- reportes --------------------------------------------------------------
export const reports = {
  dashboard: (branchId: string, from?: string, to?: string) =>
    rpc<DashboardMetrics>("dashboard_metrics", { p_branch_id: branchId, p_from: from ?? null, p_to: to ?? null }),
  sales: (branchId?: string, from?: string, to?: string, topN = 10) =>
    rpc<SalesReport>("report_sales", { p_branch_id: branchId ?? null, p_from: from ?? null, p_to: to ?? null, p_top_n: topN }),
  inventoryKpis: (branchId?: string, deadDays = 90, from?: string, to?: string) =>
    rpc<InventoryKpis>("report_inventory_kpis", { p_branch_id: branchId ?? null, p_dead_days: deadDays, p_from: from ?? null, p_to: to ?? null }),
};

// ----- tipos de retorno (resumen; el detalle completo lo da database.types.ts) -
export interface PosResolved { product_id: string; sku: string; name: string; itbms_rate: string; product_unit_id: string; factor: number; price: number | null; qty_available: number }
export interface CatalogItem {
  id: string; sku: string; name: string; itbms_rate: string;
  qty_base: number; qty_available: number;
  category_id: string | null; category_name: string | null;
  units: { product_unit_id: string; unit_id: string; unit_code: string; unit_name: string; factor: number; price: number | null }[];
}
export interface StockRow { product_id: string; sku: string; name: string; qty_base: number; qty_available: number; is_low: boolean }
export interface Sale { id: string; status: string; total: number; itbms_total: number; subtotal: number; discount_total?: number }
export interface ParkedSale { id: string; park_label: string | null; parked_at: string | null; total: number; status: string; created_at: string }
export interface SaleLine { id: string; qty_base: number; itbms_amount: number; line_total: number }
export interface Product { id: string; sku: string; name: string }
export interface Customer { id: string; name: string; tax_receiver_type: string; ruc?: string | null; dv?: string | null; cedula?: string | null; phone?: string | null; email?: string | null; address?: string | null; commercial_segment?: string; price_list_id?: string | null }
export interface CashSession { id: string; status: string; opening_amount: number }
export interface FiscalDoc { id: string; cufe: string; status: string }
export interface DashboardMetrics { sales_count: number; sales_total: number; gross_margin: number; low_stock_count: number; open_alerts: number; credit_outstanding: number }
export interface SalesReport { totals: Record<string, number>; by_method: Record<string, number>; top_products: unknown[]; by_day: unknown[] }
export interface InventoryKpis { inventory_value: number; dead_stock: { count: number; value: number }; gmroi_approx: number | null }
export interface CustomerStatement { customer: unknown; credit: unknown; aging: Record<string, number>; transactions: unknown[] }
export interface PlanLimits { branches: number | null; users: number | null; terminals: number | null; skus: number | null; docs_month: number | null }
interface ProductUpsert { id?: string; sku: string; name: string; baseUnitId: string; itbmsRate?: string; categoryId?: string; brand?: string; tracksStock?: boolean; tracksLot?: boolean; allowsFraction?: boolean; unspscCode?: string; isActive?: boolean }
interface SupplierUpsert { id?: string; name: string; ruc?: string; dv?: string; phone?: string; email?: string; paymentTermsDays?: number; leadTimeDays?: number }
interface CustomerUpsert { id?: string; name: string; taxReceiverType?: string; commercialSegment?: string; ruc?: string; dv?: string; cedula?: string; phone?: string; email?: string; address?: string; priceListId?: string }
interface PoLine { product_id: string; product_unit_id: string; qty: number; unit_cost: number }
interface ReceiptLine { po_line_id?: string; product_id: string; qty_base: number; qty_damaged?: number; unit_cost: number; lot_id?: string; damage_note?: string }
interface ReceiveOpts { supplierId?: string; branchId?: string; invoiceNo?: string; invoiceDate?: string; invoiceTotal?: number }
interface ReturnLine { sale_line_id: string; qty_base: number; restock?: boolean }
